import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../stores/authStore';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function Login() {
	const [showPassword, setShowPassword] = useState(false);
	const navigate = useNavigate();
	const { login, isLoading } = useAuthStore();
	
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm();

	const onSubmit = async (data) => {
		const result = await login(data);
		
		if (result.success) {
			toast.success('登录成功！');
			navigate('/dashboard');
		} else {
			toast.error(result.error);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full space-y-8">
				<div>
					<div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
						<svg
							className="h-8 w-8 text-primary-600"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
					</div>
					<h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
						登录您的账户
					</h2>
					<p className="mt-2 text-center text-sm text-gray-600">
						Facebook AI 广告优化系统
					</p>
				</div>
				
				<form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
					<div className="space-y-4">
						<div>
							<label htmlFor="username" className="label">
								用户名或邮箱
							</label>
							<input
								id="username"
								type="text"
								autoComplete="username"
								className={errors.username ? 'input-error' : 'input'}
								placeholder="请输入用户名或邮箱"
								{...register('username', {
									required: '请输入用户名或邮箱',
									minLength: {
										value: 3,
										message: '用户名至少需要3个字符'
									}
								})}
							/>
							{errors.username && (
								<p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
							)}
						</div>

						<div>
							<label htmlFor="password" className="label">
								密码
							</label>
							<div className="relative">
								<input
									id="password"
									type={showPassword ? 'text' : 'password'}
									autoComplete="current-password"
									className={errors.password ? 'input-error pr-10' : 'input pr-10'}
									placeholder="请输入密码"
									{...register('password', {
										required: '请输入密码',
										minLength: {
											value: 6,
											message: '密码至少需要6个字符'
										}
									})}
								/>
								<button
									type="button"
									className="absolute inset-y-0 right-0 pr-3 flex items-center"
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? (
										<EyeSlashIcon className="h-5 w-5 text-gray-400" />
									) : (
										<EyeIcon className="h-5 w-5 text-gray-400" />
									)}
								</button>
							</div>
							{errors.password && (
								<p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
							)}
						</div>
					</div>

					<div>
						<button
							type="submit"
							disabled={isLoading}
							className="btn-primary w-full flex justify-center py-3"
						>
							{isLoading ? (
								<>
									<div className="loading-spinner mr-2"></div>
									登录中...
								</>
							) : (
								'登录'
							)}
						</button>
					</div>

					<div className="text-center">
						<p className="text-sm text-gray-600">
							还没有账户？{' '}
							<Link
								to="/register"
								className="font-medium text-primary-600 hover:text-primary-500"
							>
								立即注册
							</Link>
						</p>
					</div>
				</form>

				{/* 功能说明 */}
				<div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
					<h3 className="text-sm font-medium text-blue-800 mb-2">系统功能</h3>
					<ul className="text-xs text-blue-700 space-y-1">
						<li>• Facebook Page Like 广告自动优化</li>
						<li>• AI 智能预算调整和创意管理</li>
						<li>• 实时数据监控和决策日志</li>
						<li>• 安全防护和紧急停止机制</li>
					</ul>
				</div>
			</div>
		</div>
	);
}

export default Login;
